/* eslint-disable no-console */

import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  increment,
  arrayUnion,
} from "firebase/firestore";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  getToken as getAppCheckToken,
} from "firebase/app-check";

import { getFunctions, httpsCallable } from "firebase/functions";

/* ------------------------------------------------------------------ */
/* Firebase Config                                                     */
/* ------------------------------------------------------------------ */
const firebaseConfig = {
  apiKey: "AIzaSyDtKzm_404cZ7uKoqOI2r-oP0c_NgLZB48",
  authDomain: "yourstoryworld-df05d.firebaseapp.com",
  projectId: "yourstoryworld-df05d",
  // storageBucket: "yourstoryworld-df05d.appspot.com",
  storageBucket: "yourstoryworld-df05d.firebasestorage.app",
  messagingSenderId: "1049526142342",
  appId: "1:1049526142342:web:1bae108e442f2013fc5807",
};

// Initialize app once
export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

/* ------------------------------------------------------------------ */
/* App Check (safe, quiet)                                            */
/* ------------------------------------------------------------------ */
const isLocalhost =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

try {
  const wantDebugInProd = localStorage.getItem("YSW_APPCHECK_DEBUG") === "1";
  if (isLocalhost || wantDebugInProd) {
    // eslint-disable-next-line no-undef
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
} catch { /* ignore */ }

try {
  const siteKey =
    (typeof import.meta !== "undefined" &&
      (import.meta.env?.APPCHECK_SITE_KEY || import.meta.env?.VITE_APPCHECK_SITE_KEY)) ||
    process.env.APPCHECK_SITE_KEY ||
    process.env.REACT_APP_APPCHECK_SITE_KEY ||
    "";

  if (siteKey) {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } else if (!isLocalhost) {
    console.warn("[AppCheck] No APPCHECK_SITE_KEY set. (OK while unenforced)");
  }
} catch (e) {
  console.warn("[AppCheck] init skipped:", e?.message || e);
}

/* ------------------------------------------------------------------ */
/* Core singletons                                                     */
/* ------------------------------------------------------------------ */
export const auth = getAuth(app);
export const db = getFirestore(app);
export const googleProvider = new GoogleAuthProvider();


// Region must match your deploy region
export const functions = getFunctions(app, "us-central1");

// Handy handle for quick console checks (token, user, etc.)
if (typeof window !== "undefined") window.ysw = { auth };

/* ------------------------------------------------------------------ */
/* Auth persistence                                                   */
/* ------------------------------------------------------------------ */
setPersistence(auth, browserLocalPersistence)
  .then(() => console.log("Auth persistence set to local."))
  .catch((err) => console.error("Failed to set auth persistence:", err));

/* ------------------------------------------------------------------ */
/* Auth readiness helper                                              */
/* ------------------------------------------------------------------ */
let __authReady;
async function waitForAuth() {
  if (__authReady) return __authReady;
  __authReady = new Promise((resolve) => {
    const off = auth.onAuthStateChanged(() => {
      off();
      resolve();
    });
  });
  return __authReady;
}

/* ------------------------------------------------------------------ */
/* ✅ NEW: school resolver/creator                                     */
/* ------------------------------------------------------------------ */

// Heuristic: does this user look like a school purchaser/admin?
function looksLikeSchoolUser(uDoc) {
  const sub = uDoc?.subscription || uDoc?.plan || {};
  const key = String(sub.packageKey || sub.planId || "").toLowerCase();
  const kind = String(sub.packageKind || "").toLowerCase();
  const sel = (localStorage.getItem("selectedPlan") || "").toLowerCase();
  return (
    kind === "school" ||
    key.startsWith("school_") ||
    sel.startsWith("school_") ||
    uDoc?.role === "school_admin"
  );
}

 // Find any existing school for this user, using only direct gets (no queries)
 async function findExistingSchoolId(uid) {
   // users/{uid}: prefer explicit linkage
   const uSnap = await getDoc(doc(db, "users", uid));
   if (uSnap.exists()) {
     const ud = uSnap.data() || {};
     if (ud.schoolId) return ud.schoolId;   // primary
     if (ud.orgId)    return ud.orgId;      // legacy field fallback
   }
 
   // teachers/{uid}: some setups stamp schoolId here
   const tSnap = await getDoc(doc(db, "teachers", uid));
   if (tSnap.exists() && tSnap.data()?.schoolId) return tSnap.data().schoolId;
 
   // Legacy: some older tenants used /schools/{uid}
   const sSnap = await getDoc(doc(db, "schools", uid));
   if (sSnap.exists()) return uid;
 
   // 🔇 No collection-wide queries here (would require list permissions)
   return null;
 }

 // Create a default school document for a school purchaser
 // Use deterministic id = uid and include owner/admins (required by rules)
 async function createSchoolForUser(uid, { orgName = "New School", students = 50 } = {}) {
   const schoolId = uid; // deterministic; aligns with rules & legacy reads
 
   await setDoc(
     doc(db, "schools", schoolId),
     {
       name: orgName || "New School",
       ownerUid: uid,              // ✅ required by your CREATE rule
       admins: [uid],
       package: {
         packageKey: "school_custom",
         students,
         billing: "monthly",
         currentPeriodEnd: null,
       },
       pool: {
         month: new Date().toISOString().slice(0, 7),
         included: students * 60,
         used: 0,
       },
       createdAt: serverTimestamp(),
     },
     { merge: true }
   );
 
   await setDoc(doc(db, "users", uid), { role: "school_admin", schoolId }, { merge: true });
 
   try { localStorage.setItem("schoolId", schoolId); } catch {}
   return schoolId;
 }


/**
 * Ensure the currently signed-in user has a schoolId if they are on a school plan.
 * - Finds an existing school if present.
 * - Otherwise creates one and stamps users/{uid}.schoolId + role.
 * - Caches localStorage.schoolId for the UI.
 */
export async function ensureSchoolIdForCurrentUser() {
  await waitForAuth();
  const u = auth.currentUser;
  if (!u) return null;

  // Check local cache first
  try {
    const cached = localStorage.getItem("schoolId");
    if (cached) return cached;
  } catch { /* ignore */ }

  // Load user doc
  const userRef = doc(db, "users", u.uid);
  const userSnap = await getDoc(userRef);
  const uDoc = userSnap.exists() ? (userSnap.data() || {}) : {};

  // Already stamped?
  if (uDoc.schoolId) {
    try { localStorage.setItem("schoolId", uDoc.schoolId); } catch {}
    return uDoc.schoolId;
  }

  // ⛔ Early exit for non-school users — prevents any schools/* or teachers/* reads
  if (!looksLikeSchoolUser(uDoc)) return null;

  // Any existing school related to this user?
  const existing = await findExistingSchoolId(u.uid);
  if (existing) {
    try { localStorage.setItem("schoolId", existing); } catch {}
    await setDoc(userRef, { schoolId: existing }, { merge: true });
    return existing;
  }

  // Only create if the user actually looks like a school user (already guaranteed)
  const orgName = uDoc?.orgName || uDoc?.schoolName || (u.displayName || "").trim() || "New School";
  const students = Number(uDoc?.subscription?.students || 50);
  const schoolId = await createSchoolForUser(u.uid, { orgName, students: students || 50 });
  return schoolId;
}

/* ------------------------------------------------------------------ */
/* Ensure minimal user doc (rules require it to exist)                 */
/* ------------------------------------------------------------------ */
export async function ensureUserDoc() {
  await waitForAuth();
  const u = auth.currentUser;
  if (!u) return;
  const ref = doc(db, "users", u.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(
      ref,
      {
        uid: u.uid,
        email: u.email || "",
        emailLower: (u.email || "").toLowerCase(),
        ownerUid: u.uid,
        createdAt: serverTimestamp(),
        usageMonth: new Date().toISOString().slice(0, 7),
        usageUsed: 0,
        favoriteGenres: {},
        readingActivity: [],
      },
      { merge: true }
    );
  }

  // ✅ NEW: opportunistically ensure schoolId if this user is a school purchaser/admin
  try {
    await ensureSchoolIdForCurrentUser();
  } catch (e) {
    console.warn("[schools] ensureSchoolIdForCurrentUser skipped:", e?.message || e);
  }
}

/* ------------------------------------------------------------------ */
/* Analytics helpers                                                   */
/* ------------------------------------------------------------------ */
export async function recordUserAnalytics({
  units = 1,
  reason = "story_generate",
  title = "",
  genre = "",
  durationMs = 0,
  words = 0,
} = {}) {
  await waitForAuth();
  const u = auth.currentUser;
  if (!u) return;
  const uid = u.uid;

  try {
    await addDoc(collection(db, "users", uid, "usageLogs"), {
      uid,
      reason,
      units,
      title,
      genre,
      words,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn("[metrics] usageLogs add failed (non-fatal):", e?.message || e);
  }

  const mins = Math.max(0, Math.round(durationMs / 60000));
  const userRef = doc(db, "users", uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(
      userRef,
      {
        uid,
        email: u.email || "",
        emailLower: (u.email || "").toLowerCase(),
        ownerUid: uid,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  const prev = snap.exists() ? snap.data() || {} : {};
  const prevActivity = Array.isArray(prev.readingActivity)
    ? prev.readingActivity
    : [];
  const nextActivity = [...prevActivity, { t: Date.now(), v: mins }].slice(-100);

  const newStories = Number(prev.storiesRead || 0) + 1;
  const newTotal = Number(prev.totalReadingTime || 0) + mins;

  const updates = {
    storiesRead: increment(1),
    totalReadingTime: increment(mins),
    averageReadingTime: newStories > 0 ? Math.round(newTotal / newStories) : 0,
    readingActivity: nextActivity,
  };
  if (genre) updates.genresExplored = arrayUnion(genre);

  try {
    await updateDoc(userRef, updates);
  } catch (e) {
    console.warn("[metrics] users update failed:", e?.message || e);
  }
}

/* ------------------------------------------------------------------ */
/* HTTP v2 endpoints (unified)                                        */
/* ------------------------------------------------------------------ */

const BASE = "https://us-central1-yourstoryworld-df05d.cloudfunctions.net";

const ENDPOINTS = {
  // Usage (consumer)
  getUsageHttp:             { url: `${BASE}/getUsageHttp`,             method: "GET"  },
  consumeStoryHttp:         { url: `${BASE}/consumeStoryHttp`,         method: "POST" },

  // Billing portal + checkout (Stripe)
  createPortalSessionHttp:  { url: `${BASE}/createPortalSessionHttp`,  method: "POST" },
  createCheckoutSessionHttp:{ url: `${BASE}/createCheckoutSessionHttp`,method: "POST" },

  // School endpoints
  consumeStorySchool:       { url: `${BASE}/consumeStorySchool`,       method: "POST" },

  simplifyDefinitionHttp:     { url: `${BASE}/simplifyDefinitionHttp`,     method: "POST" },

};

/* ------------------------------------------------------------------ */
/* Callable wrapper with HTTP path for specific endpoints              */
/* ------------------------------------------------------------------ */

const CALLABLES = new Set([
  "getUsage",
  "consumeStory",
  "createPortalSession",
  "createCheckoutSession",
  "createStudentSeatChecked",
  "deleteStudentSeatCounted",
  "attachSubscriptionFromSession",
  "whoAmI",
  "consumeStorySchool",
  "adminDeleteAccount",
]);

async function getIdTokenOrThrow() {
  await waitForAuth();
  if (!auth.currentUser) throw new Error("Not signed in");
  return auth.currentUser.getIdToken(/* forceRefresh */ false);
}

async function maybeGetAppCheckHeader() {
  try {
    const token = await getAppCheckToken();
    if (token?.token) return { "X-Firebase-AppCheck": token.token };
  } catch { /* ignore */ }
  return {};
}

async function callHttp(name, payload = {}) {
  const entry = ENDPOINTS[name];
  if (!entry) throw new Error(`Unknown HTTP function: ${name}`);

  const token = await getIdTokenOrThrow();
  const appCheck = await maybeGetAppCheckHeader();

  const headers = {
    ...(entry.method !== "GET" ? { "Content-Type": "application/json" } : {}),
    Authorization: `Bearer ${token}`,
    ...appCheck,
  };

  const init = { method: entry.method, headers };
  if (entry.method !== "GET") {
    init.body = JSON.stringify(payload || {});
  }

  const res = await fetch(entry.url, init);
  let data = {};
  try { data = await res.json(); } catch { /* allow empty */ }

  if (!res.ok) {
    const msg = data?.error?.message || data?.error || `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }
  return data;
}

export async function callFunction(name, payload = {}) {
  // 🔁 Legacy → HTTP v2 name aliases (keeps StoryAppUser unchanged)
  const ALIAS = {
    consumeStory: "consumeStoryHttp",
    getUsage: "getUsageHttp",
  };
  const routedName = ENDPOINTS[name] ? name : (ENDPOINTS[ALIAS[name]] ? ALIAS[name] : name);

  if (ENDPOINTS[routedName]) {
    return callHttp(routedName, payload);
  }
  if (!CALLABLES.has(name)) {
    throw new Error(`Unknown callable: ${name}`);
  }
  const fn = httpsCallable(functions, name);
  const res = await fn(payload);
  return res?.data ?? {};
}

/** Admin: delete Auth user + Firestore (kind: 'user' | 'school') */
export async function adminDeleteAccount(uid, kind = "user") {
  return await callFunction("adminDeleteAccount", { uid, kind });
}

